/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { UtrvStatus } from '../../constants/status';
import { generateUrl } from '../../routes/util';
import {
  SurveyRange,
  CustomReportType,
  NewDateComparisonReport,
  CustomReportProps,
} from '../../types/custom-report';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { FormGenerator, FieldProps } from '@g17eco/molecules/form';
import { CustomReportRoutes } from './CustomReportRoutes';
import { DateSurveySelectionTable } from './partials/DateSurveySelectionTable';
import './styles.scss';
import { convertToUtrvStatuses, FormData, getInitialState } from './util/downloadReportHandler';
import { QUESTION, SURVEY } from '@constants/terminology';
import { useCreateCustomReportMutation, useUpdateCustomReportMutation } from '@api/custom-reports';
import { DownloadUtrvStatusCombined } from '@g17eco/types/common';

const fields: FieldProps[] = [
  {
    code: 'name',
    type: 'text',
    label: 'Name of date comparison report',
    placeholder: 'For example: January to December 2022 review',
    required: true,
  },
  {
    code: 'description',
    type: 'textarea',
    label: 'Description',
    placeholder: 'Provide a description here...',
  },
  {
    code: 'status',
    type: 'radio',
    label: 'Data I want to download:',
    placeholder: '',
    options: [
      {
        value: DownloadUtrvStatusCombined.All,
        label: `All ${QUESTION.PLURAL} (including unanswered)`,
      },
      {
        value: DownloadUtrvStatusCombined.AllAnswered,
        label: `All answered ${QUESTION.PLURAL} (answered and verified)`,
      },
      {
        value: UtrvStatus.Verified,
        label: `Verified ${QUESTION.PLURAL} only (excludes assured)`,
      },
      {
        value: DownloadUtrvStatusCombined.Assured,
        label: `Assured ${QUESTION.PLURAL} (includes restated)`,
      },
    ]
  },
];

const isFormValid = (formData?: Partial<FormData>, surveyIds?: string[]) => {
  return formData?.name && surveyIds && surveyIds.length > 0;
};

const CustomReportEditFormDate = ({customReport, initiativeId}: CustomReportProps<SurveyRange>) => {
  const history = useHistory();
  const [formData, setFormData] = useState<Partial<FormData>>(getInitialState(customReport));
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);

  React.useEffect(() => {
    setFormData(getInitialState(customReport));
    setSelectedSurveyIds(customReport?.survey?.ids ?? []);
  }, [customReport]);

  const [createCustomReport, { isLoading: isCreating }] = useCreateCustomReportMutation();
  const [updateCustomReport, { isLoading: isUpdating }] = useUpdateCustomReportMutation();
  const isSaving = isCreating || isUpdating;

  const handleUpdate = (e: React.ChangeEvent<{ name: string; value: string | number | undefined }>) => {
    const { name, value } = e.target;

    if (!fields.map((f) => f.code).includes(name)) {
      return;
    }

    setFormData({
      ...(formData ?? {}),
      [name]: value,
    });
  };

  const isEditMode = Boolean(formData?._id);

  const handleCancel = () => {
    const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
    history.push(url);
  };

  const handleSubmit = async () => {
    if (!isFormValid(formData, selectedSurveyIds)) {
      return;
    }

    const data: NewDateComparisonReport = {
      type: CustomReportType.Survey,
      name: formData.name ?? '',
      description: formData.description,
      survey: {
        ids: selectedSurveyIds,
        ...convertToUtrvStatuses(formData.status),
      },
    };

    const method = formData._id
      ? updateCustomReport({initiativeId, reportId: formData._id, formData: data})
      : createCustomReport({initiativeId, formData: data});
    method
      .unwrap()
      .then(() => {
        setFormData({});
        setSelectedSurveyIds([]);
        const url = generateUrl(CustomReportRoutes.ROOT, { initiativeId });
        history.push(url);
      });
  };

  return (
    <>
      <FormGenerator
        form={{ ...formData }}
        fields={fields.map((f) => ({ ...f, disabled: isSaving }))}
        updateForm={handleUpdate}
      />

      <div className='mt-5'>
        <hr />
        <div className='mt-4 strong'>Please select which {SURVEY.PLURAL} you would like to compare:</div>
        <div className='mt-3 downloads-table-container'>
          <DateSurveySelectionTable
            initiativeId={initiativeId}
            defaultSelected={customReport?.survey?.ids}
            selectedSurveyIds={selectedSurveyIds}
            handleSelectSurveys={(ids: string[]) => setSelectedSurveyIds(ids)}
          />
        </div>
      </div>
      <div className='mt-4 text-right'>
        <Button color='link-secondary' onClick={handleCancel} className='mr-4'>
          Cancel
        </Button>
        <SubmitButton
          disabled={!isFormValid(formData, selectedSurveyIds) || isSaving}
          color='primary'
          onClick={handleSubmit}
        >
          {isEditMode ? 'Update' : 'Create'}
        </SubmitButton>
      </div>
    </>
  );
};

export default CustomReportEditFormDate;
